// src/pages/student/UserProfilePage.js
import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Avatar,
    Grid,
    Chip,
    LinearProgress,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import {
    Edit as EditIcon,
    Download as DownloadIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Pending as PendingIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import rankingService from "../../services/rankingService";
import learningService from "../../services/learningService";
import creditService from "../../services/creditService";
import { getUserEvents } from "../../services/eventService";
import { updateDocument } from "../../firebase/firestore";

export default function UserProfilePage() {
    const { userProfile } = useAuth();
    const [loading, setLoading] = useState(true);
    const [rankData, setRankData] = useState(null);
    const [progresses, setProgresses] = useState([]);
    const [pathsMap, setPathsMap] = useState({}); // ✅ NEW: Store path names
    const [credits, setCredits] = useState(0);
    const [userEvents, setUserEvents] = useState([]);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editForm, setEditForm] = useState({
        firstName: "",
        lastName: "",
        phone: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                if (userProfile?.uid) {
                    // ✅ Fetch all learning paths first
                    const allPaths = await learningService.getLearningPaths(false);
                    const pathMap = {};
                    allPaths.forEach((path) => {
                        pathMap[path.id] = path;
                    });
                    setPathsMap(pathMap);

                    // Fetch rank
                    const rank = await rankingService.getUserRank(userProfile.uid);
                    setRankData(rank);

                    // Fetch learning progress
                    const progressData = await learningService.getStudentProgressSummary(
                        userProfile.uid
                    );
                    setProgresses(progressData);

                    // Fetch credits
                    const totalCredits = await creditService.getUserTotalCredits(
                        userProfile.uid
                    );
                    setCredits(totalCredits);

                    // Fetch user events
                    const studentId = userProfile.studentId || userProfile.uid;
                    const events = await getUserEvents(studentId);
                    setUserEvents(events);
                }

                setEditForm({
                    firstName: userProfile?.firstName || "",
                    lastName: userProfile?.lastName || "",
                    phone: userProfile?.phone || "",
                });
            } catch (error) {
                console.error("Error fetching profile data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userProfile]);

    const handleSaveProfile = async () => {
        try {
            await updateDocument("users", userProfile.uid, {
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                phone: editForm.phone,
            });
            setOpenEditDialog(false);
            window.location.reload();
        } catch (error) {
            console.error("Error saving profile:", error);
        }
    };

    const getTotalLevelsCleared = () => {
        return progresses.reduce(
            (sum, p) =>
                sum +
                Object.values(p.levelStatus || {}).filter((s) => s.cleared).length,
            0
        );
    };

    const getCompletedPathsCount = () => {
        return progresses.filter((p) => p.pathCompleted).length;
    };

    // ✅ NEW: Get path name from pathId
    const getPathName = (pathId) => {
        return pathsMap[pathId]?.name || `Path ${pathId}`;
    };

    const getCertificateButton = (event) => {
        const reg = event.userRegistration;

        // If certificate exists, show download button
        if (reg?.certificateUrl) {
            return (
                <Button
                    size="small"
                    variant="contained"
                    href={reg.certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<DownloadIcon />}
                    sx={{
                        backgroundColor: "#28a745",
                        "&:hover": { backgroundColor: "#218838" },
                    }}
                >
                    Download
                </Button>
            );
        }

        // If attended but certificate not uploaded yet
        if (reg?.attended) {
            return (
                <Chip
                    icon={<PendingIcon />}
                    label="Certificate Pending"
                    color="warning"
                    size="small"
                />
            );
        }

        // Not attended
        return (
            <Typography variant="caption" color="textSecondary">
                Not Attended
            </Typography>
        );
    };

    if (loading) {
        return (
            <AdminLayout title="My Profile">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "60vh",
                    }}
                >
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="My Profile">
            <Box>
                {/* Profile Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 3,
                                mb: 3,
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    backgroundColor: "#003366",
                                    fontSize: "2rem",
                                }}
                            >
                                {userProfile?.firstName?.charAt(0) || "U"}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    {userProfile?.firstName} {userProfile?.lastName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {userProfile?.email}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Role: {userProfile?.role || "Student"}
                                </Typography>
                                {userProfile?.studentId && (
                                    <Typography variant="body2" color="textSecondary">
                                        ID: {userProfile.studentId}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => setOpenEditDialog(true)}
                                sx={{
                                    color: "#003366",
                                    borderColor: "#003366",
                                }}
                            >
                                Edit Profile
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="overline" color="textSecondary">
                                    YOUR GLOBAL RANK
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700, color: "#003366" }}>
                                    #{rankData?.rank || "-"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Out of {rankData?.totalStudents || 0} students
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="overline" color="textSecondary">
                                    TOTAL CREDITS
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700, color: "#C1272D" }}>
                                    {credits}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Points earned
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="overline" color="textSecondary">
                                    LEVELS CLEARED
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700, color: "#28a745" }}>
                                    {getTotalLevelsCleared()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Levels Cleared
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="overline" color="textSecondary">
                                    LEARNING PATHS
                                </Typography>
                                <Typography variant="h3" sx={{ fontWeight: 700, color: "#007bff" }}>
                                    {getCompletedPathsCount()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Paths Completed
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Learning Progress */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            📚 Learning Progress Details
                        </Typography>
                        {progresses.length === 0 ? (
                            <Typography color="textSecondary">
                                No learning paths started yet
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {progresses.map((progress) => {
                                    const clearedCount = Object.values(
                                        progress.levelStatus || {}
                                    ).filter((s) => s.cleared).length;
                                    const totalLevels = Object.keys(progress.levelStatus || {}).length;
                                    const percentage =
                                        totalLevels > 0 ? (clearedCount / totalLevels) * 100 : 0;

                                    return (
                                        <Box key={progress.id}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    mb: 1,
                                                }}
                                            >
                                                {/* ✅ FIXED: Show path name instead of ID */}
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {getPathName(progress.pathId)}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {clearedCount} of {totalLevels} levels
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={percentage}
                                                sx={{ height: 8, borderRadius: 1 }}
                                            />
                                            {progress.pathCompleted && (
                                                <Chip
                                                    label="Completed"
                                                    color="success"
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Events & Certificates */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                            📅 My Events & Certificates
                        </Typography>
                        {userEvents.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 3 }}>
                                <Typography color="textSecondary" sx={{ mb: 2 }}>
                                    You have not registered for any events yet
                                </Typography>
                                <Button
                                    variant="contained"
                                    href="/student/events"
                                    sx={{
                                        backgroundColor: "#C1272D",
                                        "&:hover": { backgroundColor: "#a01f25" },
                                    }}
                                >
                                    Browse Events
                                </Button>
                            </Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Event Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Attendance</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Certificate</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {userEvents.map((event) => (
                                            <TableRow key={event.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {event.title}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {event.eventDate
                                                            ? new Date(event.eventDate).toLocaleDateString()
                                                            : "Date TBA"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={event.eventType || "Event"}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {event.userRegistration?.attended ? (
                                                        <Chip
                                                            icon={<CheckIcon />}
                                                            label="Attended"
                                                            color="success"
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<CancelIcon />}
                                                            label="Registered"
                                                            color="info"
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>{getCertificateButton(event)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>

                {/* Edit Profile Dialog */}
                <Dialog
                    open={openEditDialog}
                    onClose={() => setOpenEditDialog(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Edit Profile</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={editForm.firstName}
                                onChange={(e) =>
                                    setEditForm((prev) => ({ ...prev, firstName: e.target.value }))
                                }
                            />
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={editForm.lastName}
                                onChange={(e) =>
                                    setEditForm((prev) => ({ ...prev, lastName: e.target.value }))
                                }
                            />
                            <TextField
                                fullWidth
                                label="Phone"
                                value={editForm.phone}
                                onChange={(e) =>
                                    setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                                }
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleSaveProfile}
                            variant="contained"
                            sx={{
                                backgroundColor: "#C1272D",
                                "&:hover": { backgroundColor: "#a01f25" },
                            }}
                        >
                            Save Changes
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
